import React, { useEffect, useState } from 'react';
import AutosuggestComponent, { RenderSuggestionsContainerParams, SuggestionSelectedEventData } from 'react-autosuggest';
import classNames from 'classnames';
import DefaultFilterInputLayout from './DefaultFilterInputLayout';
import { AutosuggestionDropdownLayoutType, Suggestion } from 'framework/form';
import { useFilterContext } from '../../../FilterContext';
import {
  getSuggestionsWarning,
  renderSuggestion,
  getSuggestionValue,
} from 'framework/ui/layout/form/input/AutosuggestionDropdownLayout/AutosuggestionDropdownLayout';
import {
  COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
  getCommonInputProps,
} from '../../../../../ui/layout/form/input/BaseInputLayout';
import BaseInputErrors from '../../../../../ui/layout/form/input/BaseInputErrors';
import SearchIcon from '@mui/icons-material/SearchOutlined';
import { useUiTheme } from 'framework/uiTheme';
import { Box } from '@mui/material';
import { autosuggestionDropdownSx } from 'framework/ui/layout/form/input/AutosuggestionDropdownLayout/autosuggestionDropdownSx';

const AutosuggestionDropdownLayout: AutosuggestionDropdownLayoutType = ({
  suggestions,
  suggestionsLoaded,
  suggestionsTotalPages,
  onSuggestionsFetchRequested,
  onSuggestionsClearRequested,
  onSuggestionSelected: parentOnSuggestionSelected,
  loading,
  noResultMessage,
  resultsPerPage,
  onChangeCallback,
  label,
  errorMessageMapper,
  errorMessages,
  ...otherProps
}) => {
  const props = {
    ...COMMON_INPUT_LAYOUT_DEFAULT_PROPS,
    ...otherProps,
  };

  const { apply, clearAndApplyFilter } = useFilterContext();
  const inputProps = getCommonInputProps(props);
  const [displayedValue, setDisplayedValue] = useState<string>(inputProps.value || '');
  const [isMenuOpened, setIsMenuOpened] = useState(false);
  const hasError = errorMessages && errorMessages.length > 0;
  const invalidClass = hasError ? ' is-invalid' : '';
  const { SmallLoader } = useUiTheme();

  const toggleMenu = () => {
    setIsMenuOpened(prevValue => !prevValue);
  };

  const resetFilter = () => {
    setDisplayedValue('');
    parentOnSuggestionSelected(
      {} as any,
      { suggestion: { id: '', text: '' }, suggestionValue: '' } as SuggestionSelectedEventData<Suggestion>,
    );
    clearAndApplyFilter(inputProps.name);
  };

  useEffect(() => {
    if (isMenuOpened && displayedValue) {
      setIsMenuOpened(false);
      apply();
    }
    // Suppressed warnings because we only need to call useEffect callback is displayedValue was updated.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayedValue]);

  const onSuggestionSelected = (
    event: React.FormEvent<object>,
    suggestionData: SuggestionSelectedEventData<Suggestion>,
  ) => {
    setDisplayedValue(suggestionData.suggestion.text);
    parentOnSuggestionSelected(event, suggestionData);
  };

  const renderSuggestionsContainer = ({ containerProps, children }: RenderSuggestionsContainerParams) => {
    const { key, ...otherContainerProps } = containerProps;

    return (
      <Box key={key} {...otherContainerProps} className={containerProps.className} position="static" border={0}>
        {suggestions.length === 0 && suggestionsLoaded && (
          <Box className="react-autosuggest__no-result">{noResultMessage}</Box>
        )}
        {suggestionsTotalPages > 1 && (
          <Box className="react-autosuggest__warning-message">{getSuggestionsWarning(resultsPerPage)}</Box>
        )}
        {children}
      </Box>
    );
  };

  return (
    <Box
      sx={[
        autosuggestionDropdownSx,
        theme => ({
          '& .autosuggest-button-dropdown': {
            '& .react-autosuggest__warning-message': {
              marginTop: '5px',
              marginBottom: '5px',
              borderTop: '1px solid',
              borderTopColor: 'border.default',
              borderBottom: '1px solid',
              borderBottomColor: 'border.default',
              backgroundColor: 'background.paper',
              fontSize: '12px',
              fontWeight: 500,
            },
            '& .react-autosuggest__no-result, & .react-autosuggest__suggestions-container': {
              position: 'static',
              border: 0,
              backgroundColor: 'background.paper',
              boxShadow: 'none',
              transform: 'none',
            },
          },
          '& .search-filter-control-wrapper': {
            position: 'relative',
            minWidth: '275px',
            [theme.breakpoints.down('sm')]: { fontSize: '14px' },
            '& .react-autosuggest-filter--loader': {
              position: 'absolute',
              top: '11px',
              right: '16px',
            },
          },
          '& .search-filter-control': {
            width: '100%',
            minHeight: '38px',
            padding: '8px 15px 8px 42px',
            border: 0,
            background: 'none',
            lineHeight: 1.25,
            [theme.breakpoints.down('sm')]: { fontSize: '14px' },
            '&:focus': {
              outline: 'none',
              boxShadow: 'none',
            },
            '&.search-filter-control--loading': { paddingRight: '45px' },
          },
        }),
      ]}
    >
      <DefaultFilterInputLayout
        source={inputProps.name}
        label={label}
        displayedValue={displayedValue}
        isOpen={isMenuOpened}
        toggleMenu={toggleMenu}
        className={classNames('autosuggest-button-dropdown', invalidClass)}
        onResetFilterCallback={resetFilter}
      >
        <Box className="search-filter-control-wrapper" sx={{ width: '100%' }}>
          <AutosuggestComponent
            suggestions={suggestions}
            onSuggestionsFetchRequested={onSuggestionsFetchRequested}
            onSuggestionsClearRequested={onSuggestionsClearRequested}
            renderSuggestionsContainer={renderSuggestionsContainer}
            onSuggestionSelected={onSuggestionSelected}
            getSuggestionValue={getSuggestionValue}
            renderSuggestion={renderSuggestion}
            inputProps={{
              ...inputProps,
              value: inputProps.value || '',
              onChange: onChangeCallback,
              className: classNames(inputProps.className, 'search-filter-control', {
                'search-filter-control--loading': loading,
              }),
              placeholder: inputProps.placeholder || `Search ${label}`,
              autoFocus: isMenuOpened,
            }}
          />
          <SearchIcon
            className="search-filter-control-icon"
            sx={{ position: 'absolute', top: '9px', left: '10px', fontSize: '22px' }}
          />
          {loading ? <SmallLoader className="react-autosuggest-filter--loader" /> : null}
        </Box>

        <BaseInputErrors errorMessages={errorMessages} errorMessageMapper={errorMessageMapper} />
      </DefaultFilterInputLayout>
    </Box>
  );
};

export default AutosuggestionDropdownLayout;
